import { useSearchParams } from "react-router-dom";
import { useAppStore } from "store/store";
import { externalLinks } from "assets/external-links/external-links";
import useMultipaxSession from "../useMultiPaxSession";
import Content, { ContentNS } from "content/content";
import { PaxCheckRequiredAction } from "api";
import { getSessionRelativeUrl } from "./useFlowRedirect";

export interface HeaderNavigationLink {
  title: string;
  url: string;
}

/*
Hook to get an array of navigation links to be shown in the header/burger menu
*/
const useHeaderLinks = (shouldShowHome: boolean): HeaderNavigationLink[] => {
  const textUI = (s: string) => Content.shared.t(s, { ns: ContentNS.ui });

  const sessionId = useAppStore((state) => state.sessionId);
  const sessionRelativeUrl = getSessionRelativeUrl(sessionId);

  const returnUrl = useAppStore((state) => state.returnUrl);

  const [searchParams] = useSearchParams();
  const queryParamsToAdd = searchParams ? `?${searchParams.toString()}` : "";

  const isMultiPax = useMultipaxSession();
  const paxSessions = useAppStore((state) => state.paxSessions);

  const anyPaxWithoutConsent = paxSessions.some(
    (paxSession) =>
      paxSession.requiredAction ===
      PaxCheckRequiredAction.PROVIDE_CONSENT_RESPONSE
  );

  const output: HeaderNavigationLink[] = [];

  if (shouldShowHome) {
    output.push({
      title: textUI("external-page.home"),
      url: externalLinks.homePage,
    });
  }

  if (returnUrl) {
    output.push({
      title: textUI("external-page.manage-my-booking"),
      url: returnUrl,
    });
  }

  if (isMultiPax && !anyPaxWithoutConsent) {
    output.push({
      title: textUI("see-all-passengers"),
      url: `${sessionRelativeUrl}/select-passenger${queryParamsToAdd}`,
    });
  }

  if (sessionId && !anyPaxWithoutConsent) {
    output.push({
      title: textUI("review-consent"),
      url: `${sessionRelativeUrl}/consent${queryParamsToAdd}`,
    });
  }

  return output;
};

export default useHeaderLinks;
